import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Button states come straight from the Foundations page: primary, secondary,
 * ghost, link-with-arrow, plus focus / disabled / loading. Every size clears
 * the 44px touch target (AC-3).
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] font-sans text-[0.9375rem] font-semibold transition-colors duration-200 ease-[var(--ease-entry)] disabled:pointer-events-none disabled:bg-ink/12 disabled:text-ink/38 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "border-0 bg-accent text-on-dark hover:bg-accent-press",
        secondary: "border border-ink/28 bg-transparent text-ink hover:bg-ink/5",
        ghost: "border-0 bg-transparent text-ink hover:bg-ink/5",
        onDark: "border-0 bg-bg text-ink hover:bg-white",
        link: "h-auto min-h-0 rounded-none border-0 border-b border-accent px-0 pb-1 text-accent hover:border-accent-link hover:text-accent-link",
        danger: "border-0 bg-danger text-on-dark hover:brightness-90",
      },
      size: {
        sm: "min-h-10 px-4 text-[0.8125rem]",
        default: "min-h-12 px-[1.625rem]",
        lg: "min-h-[3.25rem] px-8 text-base",
        icon: "size-11 px-0",
      },
      block: { true: "w-full", false: "" },
    },
    compoundVariants: [{ variant: "link", size: "default", class: "min-h-11" }],
    defaultVariants: { variant: "primary", size: "default", block: false },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, block }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
